/**
 * resizeAndEncode.js — clamp long-edge, draw to canvas, encode JPEG.
 *
 * FT-342 (2026-07-08) — capture pipeline.
 * See: Maritime-Agents/seaready-app/FT-340-photos/DESIGN-BRIEF.md §4.2 (stage 4).
 *
 * `createImageBitmap` in `exifOrientation` handles the primary resize as part
 * of the decode pass. This stage:
 *   1. Enforces the long-edge cap (belt-and-braces — the bitmap may still be
 *      slightly over target if the source was smaller than expected).
 *   2. Paints the bitmap onto a canvas at the final dimensions.
 *   3. Encodes to JPEG at the tier's quality level.
 *
 * Uses `OffscreenCanvas` when available (Chrome + Firefox + Safari 16.4+).
 * Falls back to a standard `<canvas>` for Safari < 16.4.
 */

import { encodeWithinBudget } from '../../../../utils/photoPayloadBudget';

const QUALITY_MAP = {
  standard: { longEdge: 1920, quality: 0.80 },
  high:     { longEdge: 2048, quality: 0.85 },
};

/**
 * Resize an ImageBitmap and encode to JPEG.
 * @param {ImageBitmap} bitmap
 * @param {'standard'|'high'} quality
 * @returns {Promise<{ blob: Blob, width: number, height: number, bytes: number }>}
 */
export async function resizeAndEncode(bitmap, quality = 'standard') {
  if (!bitmap || typeof bitmap.width !== 'number' || typeof bitmap.height !== 'number') {
    throw new Error('[resizeAndEncode] expected ImageBitmap input');
  }
  const tier = QUALITY_MAP[quality] || QUALITY_MAP.standard;
  const { width, height } = clampToLongEdge(bitmap.width, bitmap.height, tier.longEdge);

  const blob = await paintAndEncode(bitmap, width, height, tier.quality);
  return { blob, width, height, bytes: blob.size };
}

/**
 * FT-577: resize + encode, ENFORCING the real `/sync/push` request-body budget.
 *
 * Attempt #1 is identical to `resizeAndEncode` (same long edge, same quality),
 * so a normal photo comes out byte-for-byte as before. Only if the measured
 * request body (full image + thumbnail + record fields, as really sent) is over
 * budget does it step quality AND dimensions down (see encodeWithinBudget). If
 * nothing reasonable fits, it throws PhotoTooLargeError — the caller surfaces it.
 *
 * @param {ImageBitmap} bitmap
 * @param {'standard'|'high'} quality
 * @param {{ measure: (blob: Blob, width: number, height: number) => number, budgetBytes?: number, minLongEdge?: number }} budget
 * @returns {Promise<{ blob: Blob, width: number, height: number, bytes: number, quality: number, bodyBytes: number, attempts: number }>}
 */
export async function resizeAndEncodeWithinBudget(bitmap, quality = 'standard', budget = {}) {
  if (!bitmap || typeof bitmap.width !== 'number' || typeof bitmap.height !== 'number') {
    throw new Error('[resizeAndEncode] expected ImageBitmap input');
  }
  if (typeof budget.measure !== 'function') {
    throw new Error('[resizeAndEncode] resizeAndEncodeWithinBudget requires a measure() function');
  }
  const tier = QUALITY_MAP[quality] || QUALITY_MAP.standard;
  const result = await encodeWithinBudget({
    srcWidth: bitmap.width,
    srcHeight: bitmap.height,
    startLongEdge: tier.longEdge,
    startQuality: tier.quality,
    ...(budget.budgetBytes ? { budgetBytes: budget.budgetBytes } : {}),
    ...(budget.minLongEdge ? { minLongEdge: budget.minLongEdge } : {}),
    encode: (w, h, q) => paintAndEncode(bitmap, w, h, q),
    measure: budget.measure,
  });
  return {
    blob: result.encoded,
    width: result.width,
    height: result.height,
    bytes: result.encoded.size,
    quality: result.quality,
    bodyBytes: result.bodyBytes,
    attempts: result.attempts,
  };
}

/**
 * Preserve aspect ratio while clamping the LONG edge to `maxLongEdge`.
 * If the source is already smaller, the source dimensions are returned.
 */
export function clampToLongEdge(srcW, srcH, maxLongEdge) {
  const long = Math.max(srcW, srcH);
  if (long <= maxLongEdge) return { width: srcW, height: srcH };
  const scale = maxLongEdge / long;
  return {
    width: Math.round(srcW * scale),
    height: Math.round(srcH * scale),
  };
}

async function paintAndEncode(bitmap, width, height, quality) {
  // Prefer OffscreenCanvas + convertToBlob — no main-thread render pressure.
  if (typeof OffscreenCanvas === 'function') {
    const off = new OffscreenCanvas(width, height);
    const ctx = off.getContext('2d');
    if (!ctx) throw new Error('[resizeAndEncode] OffscreenCanvas 2d context unavailable');
    ctx.drawImage(bitmap, 0, 0, width, height);
    if (typeof off.convertToBlob === 'function') {
      return off.convertToBlob({ type: 'image/jpeg', quality });
    }
    // Fallthrough: some environments expose OffscreenCanvas but not convertToBlob.
  }
  // Fallback: DOM canvas + toBlob. Requires a document (browser or jsdom).
  if (typeof document === 'undefined') {
    throw new Error('[resizeAndEncode] no canvas backend available');
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('[resizeAndEncode] 2d context unavailable');
  ctx.drawImage(bitmap, 0, 0, width, height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('[resizeAndEncode] canvas.toBlob returned null'));
      },
      'image/jpeg',
      quality,
    );
  });
}

export default resizeAndEncode;
